import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MonoTypeOperatorFunction, Observable, from, switchMap, throwError, timeout } from 'rxjs';
import { environment } from 'src/environments/environment';
import {
  ADVANCED_SEARCH_BASELINE_COUNT,
  ADVANCED_SEARCH_MAX_COUNT,
  AdvancedSearchGenerationMode,
  AdvancedSearchPlannerAsset,
  AdvancedSearchPlannerModel,
  AdvancedSearchPlannerRequest,
  AdvancedSearchPlannerResponse,
  AdvancedSearchPreviewCard,
  ConfirmLandmarkGroundingRequest,
  ConfirmLandmarkGroundingResponse,
  DownloadAdvancedSearchAssetPairZipRequest,
  GenerateAdvancedSearchAssetPairBatchRequest,
  GenerateAdvancedSearchAssetPairBatchResponse,
  PrepareLandmarkGroundingRequest,
  PrepareLandmarkGroundingResponse,
  RecognizeReferenceBatchRequest,
  RecognizeReferenceBatchResponse,
  ResolveLandmarkReferenceUrlsRequest,
  ResolveLandmarkReferenceUrlsResponse,
  GenerateAdvancedSearchAssetLandscapeRequest,
  GenerateAdvancedSearchAssetLandscapeReplacementRequest,
  GenerateAdvancedSearchAssetLandscapeReplacementResponse,
  GenerateAdvancedSearchAssetLandscapeResponse,
  GenerateAdvancedSearchAssetRequest,
  GenerateAdvancedSearchAssetResponse,
  GenerateAdvancedSearchAssetSheetRequest,
  GenerateAdvancedSearchAssetSheetResponse,
} from 'src/app/models/advanced-search';
import { FirebaseAuthService } from 'src/app/shared/providers/firebase-auth.service';
import {
  AdminGenerationControlRequest,
  AdminGenerationControlResponse,
  StartAdminGenerationRunRequest,
  StartAdminGenerationRunResponse,
} from 'src/app/admin/models/admin-campaign.model';
import {
  AnalyzeQueryAssetsRequest,
  AnalyzeQueryAssetsResponse,
} from 'src/app/admin/models/query-analysis.model';
import {
  AnalyzeGroupAssetsRequest,
  AnalyzeGroupAssetsResponse,
} from 'src/app/admin/models/group-analysis.model';
import {
  SuggestAssetNamesRequest,
  SuggestAssetNamesResponse,
} from 'src/app/admin/models/asset-naming.model';

@Injectable({
  providedIn: 'root'
})
export class AdvancedSearchService {
  // A hard backstop, not a policy: the slowest function here is capped at 900s server-side
  // (analyzeGroupAssets), so this can only ever end a request the backend has already given
  // up on. Without it a stalled response hangs forever — HttpClient has no default timeout,
  // and the caller's `finally` never runs, so a panel spinner stays up with every button
  // disabled and no error anywhere. Seen with the Functions emulator, whose proxy pipe can
  // pause mid-body and never resume.
  private static readonly REQUEST_TIMEOUT_MS = 930_000;

  private readonly functionsBaseUrl = this.resolveFunctionsBaseUrl();

  constructor(
    private http: HttpClient,
    private firebaseAuth: FirebaseAuthService,
  ) {}

  public planAssets(
    prompt: string,
    baselineCount: number = ADVANCED_SEARCH_BASELINE_COUNT,
    maxCount: number = ADVANCED_SEARCH_MAX_COUNT,
    generationMode?: AdvancedSearchGenerationMode,
    plannerModel?: AdvancedSearchPlannerModel,
    plannerTemperature?: number,
  ) {
    const request: AdvancedSearchPlannerRequest = {
      prompt,
      baselineCount,
      maxCount,
      generationMode,
      plannerModel,
      plannerTemperature,
    };

    return this.postJsonWithAuth<AdvancedSearchPlannerResponse>('planAdvancedSearchAssets', request);
  }

  public generateAssetPreview(request: GenerateAdvancedSearchAssetRequest) {
    return this.postJsonWithAuth<GenerateAdvancedSearchAssetResponse>('generateAdvancedSearchAsset', request);
  }

  public generateAssetSheet(request: GenerateAdvancedSearchAssetSheetRequest) {
    return this.postJsonWithAuth<GenerateAdvancedSearchAssetSheetResponse>('generateAdvancedSearchAssetSheet', request);
  }

  public generateAssetSheetPro(request: GenerateAdvancedSearchAssetSheetRequest) {
    return this.postJsonWithAuth<GenerateAdvancedSearchAssetSheetResponse>('generateAdvancedSearchAssetSheetPro', request);
  }

  public generateAssetLandscape(request: GenerateAdvancedSearchAssetLandscapeRequest) {
    return this.postJsonWithAuth<GenerateAdvancedSearchAssetLandscapeResponse>(
      'generateAdvancedSearchAssetLandscape',
      request,
    );
  }

  public generateAssetLandscapeReplacement(request: GenerateAdvancedSearchAssetLandscapeReplacementRequest) {
    return this.postJsonWithAuth<GenerateAdvancedSearchAssetLandscapeReplacementResponse>(
      'generateAdvancedSearchAssetLandscapeReplacement',
      request,
    );
  }

  public generateAssetPairBatch(request: GenerateAdvancedSearchAssetPairBatchRequest) {
    return this.postJsonWithAuth<GenerateAdvancedSearchAssetPairBatchResponse>(
      'generateAdvancedSearchAssetPairBatch',
      request,
    );
  }

  public downloadAssetPairZip(request: DownloadAdvancedSearchAssetPairZipRequest) {
    return this.postBlobWithAuth('downloadAdvancedSearchAssetPairZip', request);
  }

  // Landmark grounding: find + verify reference photos and a factual description
  // for a real building/sculpture named by the asset title.
  public prepareLandmarkGrounding(request: PrepareLandmarkGroundingRequest) {
    return this.postJsonWithAuth<PrepareLandmarkGroundingResponse>('prepareLandmarkGrounding', request);
  }

  // Persist an admin-confirmed grounding (uploads selected photos, caches the doc).
  public confirmLandmarkGrounding(request: ConfirmLandmarkGroundingRequest) {
    return this.postJsonWithAuth<ConfirmLandmarkGroundingResponse>('confirmLandmarkGrounding', request);
  }

  // Batch references: name a whole batch of dropped reference photos in one call,
  // in the context of the query and group they are joining.
  public recognizeReferenceBatch(request: RecognizeReferenceBatchRequest) {
    return this.postJsonWithAuth<RecognizeReferenceBatchResponse>('recognizeReferenceBatch', request);
  }

  public resolveLandmarkReferenceUrls(request: ResolveLandmarkReferenceUrlsRequest) {
    return this.postJsonWithAuth<ResolveLandmarkReferenceUrlsResponse>('resolveLandmarkReferenceUrls', request);
  }

  // Kick off a browser-independent generation run on the backend (Cloud Tasks
  // fan-out). Returns immediately with the runId; progress is reflected through
  // the Firestore run doc, so the tab can be closed.
  public startAdminGenerationRun(request: StartAdminGenerationRunRequest) {
    return this.postJsonWithAuth<StartAdminGenerationRunResponse>('startAdminGenerationRun', request);
  }

  // Semantic control-plane commands. The browser sends intent, while the
  // function owns Firestore transitions, timestamps and audit records.
  public controlAdminGeneration(request: AdminGenerationControlRequest) {
    return this.postJsonWithAuth<AdminGenerationControlResponse>('controlAdminGeneration', request);
  }

  // Admin Content analysis: sends the preview images a query already produced and
  // returns alternative search queries those assets would satisfy. Read-only — it
  // generates no images and persists nothing.
  public analyzeQueryAssets(request: AnalyzeQueryAssetsRequest) {
    return this.postJsonWithAuth<AnalyzeQueryAssetsResponse>('analyzeQueryAssets', request);
  }

  // Group-level analysis: sends every preview in a group and returns new search
  // queries the group can already satisfy, favouring ones that cut across several
  // existing queries. Read-only, and slow by nature — hundreds of images per call.
  public analyzeGroupAssets(request: AnalyzeGroupAssetsRequest) {
    return this.postJsonWithAuth<AnalyzeGroupAssetsResponse>('analyzeGroupAssets', request);
  }

  public suggestAssetNames(request: SuggestAssetNamesRequest) {
    return this.postJsonWithAuth<SuggestAssetNamesResponse>('suggestAssetNames', request);
  }

  public createPlaceholderCards(assets: AdvancedSearchPlannerAsset[]): AdvancedSearchPreviewCard[] {
    return assets
      .slice()
      .sort((left, right) => left.order - right.order)
      .map((asset) => ({
        ...asset,
        status: 'pending',
      }));
  }

  private resolveFunctionsBaseUrl(): string {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname === 'localhost' || hostname === '127.0.0.1') {
        return `http://127.0.0.1:5001/${environment.firebaseConfig.projectId}/us-central1`;
      }
    }

    // Bypass Hosting/custom-domain proxy for long-running Advanced Search HTTP functions.
    // Hosting rewrites have a strict request timeout and can return 502/504 for heavy image jobs.
    return `https://us-central1-${environment.firebaseConfig.projectId}.cloudfunctions.net`;
  }

  private postJsonWithAuth<T>(path: string, body: unknown) {
    return from(this.firebaseAuth.getUserToken()).pipe(
      switchMap((token) =>
        this.http.post<T>(`${this.functionsBaseUrl}/${path}`, body, {
          headers: this.buildAuthHeaders(token),
        }),
      ),
      this.abandonAfterTimeout(path),
    );
  }

  private postBlobWithAuth(path: string, body: unknown) {
    return from(this.firebaseAuth.getUserToken()).pipe(
      switchMap((token) =>
        this.http.post(`${this.functionsBaseUrl}/${path}`, body, {
          headers: this.buildAuthHeaders(token),
          responseType: 'blob',
        }),
      ),
      this.abandonAfterTimeout(path),
    );
  }

  // The message names the work as possibly *done*: these calls are paid, and an admin who
  // reads "failed" re-runs them. The backend log is the only place that says which it was.
  private abandonAfterTimeout<T>(path: string): MonoTypeOperatorFunction<T> {
    return timeout<T, Observable<never>>({
      each: AdvancedSearchService.REQUEST_TIMEOUT_MS,
      with: () =>
        throwError(() => new Error(
          `${path} sent no response within ` +
            `${Math.round(AdvancedSearchService.REQUEST_TIMEOUT_MS / 1000)}s, so the request was abandoned. ` +
            'The backend may still have completed the work — check the function logs before paying for a re-run.',
        )),
    });
  }

  private buildAuthHeaders(token: string): HttpHeaders {
    return new HttpHeaders({
      Authorization: `Bearer ${token}`,
    });
  }
}
